import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_UPDATE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  GeneralUpdateLocation,
  GeneralUpdateSettingsSchedulerRebootDay,
  ModuleId,
  SectionId,
  WeekDays,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  getRepeatIntervalFormOptionsForSelection,
  getWeekDaysFormOptions,
} from '../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { GzTimePickerWebComponent } from '../../../../../../../ui/gzTimePicker';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzMultiSelectGroupWebComponent } from '../../../../../../../ui/gzMultiSelect';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../types/common/types';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { RepeatInterval } from '@nirvana/shared/dtos/types/policies/common';
import {
  GeneralUpdateFormValue,
  GeneralUpdateSchedulerType,
  GeneralUpdateSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import {
  GENERAL_UPDATE_DEFAULT_VALUE,
  getGeneralUpdateRingFormOptions,
  getGeneralUpdateSearchableKeywordsSearchableKeywords,
  getGeneralUpdateSettingsSchedulerRebootDayFormOptions,
  MAX_UPDATE_INTERVAL,
  MIN_UPDATE_INTERVAL,
} from '../../../../utils/uiSettings/general/general.values';
import { GzSingleSelectWebComponent } from '../../../../../../../ui/gzSingleSelectDropdown';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { GzGeneralUpdateLocationsTableWebComponent } from '../gridComponents/GzGeneralUpdateLocationsTableWebComponent';
import { GzDialogWebComponent } from '../../../../../../../ui/gzDialog';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_UPDATE)
export class GzGeneralUpdateWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralUpdateFormValue,
  GeneralUpdateSearchableKeywords
> {
  /**
   * Path for this section.
   */
  protected path = {
    moduleId: ModuleId.GENERAL,
    sectionId: SectionId.UPDATE,
  };

  /**
   * Searchable keywords for this section.
   */
  protected searchableKeywords = getGeneralUpdateSearchableKeywordsSearchableKeywords();

  @query('#general-update-product-enabled-field')
  private productEnabledField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#general-update-product-content')
  private productContent: HTMLElement;
  @query('#general-update-product-occurrence-field')
  private productOccurrenceField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('#general-update-product-week-days-field')
  private productWeekDaysField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent>;
  @query('#general-update-product-time-section')
  private productTimeSection: HTMLElement;
  @query('#general-update-product-start-time-field')
  private productStartTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#general-update-product-end-time-field')
  private productEndTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#general-update-product-update-interval-field')
  private productUpdateIntervalField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#general-update-product-postpone-reboot-field')
  private productPostponeRebootField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-update-product-reboot-enable-field')
  private productRebootEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-update-product-reboot-day-field')
  private productRebootDayField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-update-product-reboot-time-field')
  private productRebootTimeField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#general-update-signature-enabled-field')
  private signatureEnabledField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#general-update-signature-content')
  private signatureContent: HTMLElement;
  @query('#general-update-signature-occurrence-field')
  private signatureOccurrenceField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('#general-update-signature-week-days-field')
  private signatureWeekDaysField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent>;
  @query('#general-update-signature-time-section')
  private signatureTimeSection: HTMLElement;
  @query('#general-update-signature-start-time-field')
  private signatureStartTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#general-update-signature-end-time-field')
  private signatureEndTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#general-update-signature-update-interval-field')
  private signatureUpdateIntervalField: GzFormFieldWebComponent<GzInputNumberWebComponent>;

  @query('#general-update-locations-field')
  private updateLocationsField: GzFormFieldWebComponent<
    GzGeneralUpdateLocationsTableWebComponent,
    GeneralUpdateLocation[]
  >;
  @query('#general-update-default-location-field')
  private defaultLocationField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-update-ring-field')
  private updateRingField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;

  private isCloud = Application.Main.product.isCloud();

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-form-field id="general-update-product-enabled-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.ProductUpdate].label}"
        gz-form-input
        @toggleChange="${e => toggleDisabled(this.productContent, !e.detail.checked)}"
      >
        <gz-title class="second-title">
          ${this.searchableKeywords[GeneralUpdateSearchableKeywords.ProductUpdate].translatedLabel}
        </gz-title>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText" id="general-update-product-enabled-text"
      >${translateFn(
        "Configure the Bitdefender security agent's update frequency. On Windows, product updates automatically trigger engine updates for optimal performance."
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="general-update-product-content" class="mt-4">
        <gz-title
          searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Scheduler].label}"
          fontSize="11"
          fontWeight="600"
          class="form-subtitle"
          id="general-update-scheduler-subtitle"
          >${this.searchableKeywords[GeneralUpdateSearchableKeywords.Scheduler].translatedLabel}
        </gz-title>

        ${this.renderSchedulerSection(GeneralUpdateSchedulerType.PRODUCT)}

        <gz-form-field id="general-update-product-postpone-reboot-field">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.PostponeReboot].label}"
            label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.PostponeReboot].translatedLabel}"
            gz-form-input
            @change="${e => {
              toggleDisabled(this.productRebootEnableField, !e.detail.checked);
              this.toggleRebootSettings();
            }}"
          ></gz-checkbox>
        </gz-form-field>

        <div class="checkbox-with-input">
          <gz-form-field class="checkbox-width-250" id="general-update-product-reboot-enable-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Reboot].label}"
              label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Reboot].translatedLabel + ':'}"
              gz-form-input
              @change="${this.toggleRebootSettings}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field id="general-update-product-reboot-day-field" class="disabled">
            <gz-dropdown
              .labelWidth="${LabelWidthValues.MEDIUM}"
              gz-form-input
              .options=${getGeneralUpdateSettingsSchedulerRebootDayFormOptions()}
              .value=${GeneralUpdateSettingsSchedulerRebootDay.EVERY_DAY}
            ></gz-dropdown>
          </gz-form-field>

          <span class="ml-2 mr-2" id="general-update-product-reboot-day-at">${translateFn('at')}</span>

          <gz-form-field id="general-update-product-reboot-time-field">
            <gz-time-picker gz-form-input></gz-time-picker>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>
      </div>

      <gz-form-field id="general-update-signature-enabled-field" class="mt-4 mbw-1" status-field>
        <gz-toggle
          class="content-header-toggle"
          searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.SecurityUpdate].label}"
          gz-form-input
          @toggleChange="${e => toggleDisabled(this.signatureContent, !e.detail.checked)}"
        >
          <gz-title class="second-title">
            ${this.searchableKeywords[GeneralUpdateSearchableKeywords.SecurityUpdate].translatedLabel}
          </gz-title>
        </gz-toggle
        >
      </gz-form-field>

      <gz-text fontSize="12" fontWeight="400" class="infoText" id="general-update-signature-enabled-text"
        >${translateFn(
          'Configure the frequency of security content updates, which include scan engines, machine learning models, and various threat detection components.'
        )}
      </gz-text>

      <div id="general-update-signature-content" class="mt-4">
        <gz-title
          searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Scheduler].label}"
          fontSize="11"
          fontWeight="600"
          class="form-subtitle"
          id="general-update-scheduler-subtitle"
          >${this.searchableKeywords[GeneralUpdateSearchableKeywords.Scheduler].translatedLabel}
        </gz-title>

        ${this.renderSchedulerSection(GeneralUpdateSchedulerType.SIGNATURE)}
      </div>

      <div id="general-update-locations" class="mt-4">
        <gz-title
          id="general-update-locations-title"
          searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.UpdateLocations].label}"
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.HEADING_4}"
        >
          ${this.searchableKeywords[GeneralUpdateSearchableKeywords.UpdateLocations].translatedLabel}
        </gz-title>

        <gz-text fontSize="12" fontWeight="400" class="infoText mt-1" id="general-update-locations-text"
          >${
            this.isCloud
              ? translateFn(
                  "The Bitdefender security agent's default update location is https://update-cloud.2d585.cdn.bitdefender.net. For alternative update locations, enter the IP addresses or local hostnames of relay machines in your network, and set their priority."
                )
              : translateFn(
                  "The Bitdefender security agent's default update location is the local update server. For alternative update locations, enter the IP addresses or the local hostnames of the update servers in your network, and set their priority."
                )
          }
        </gz-text>

        <gz-form-field id="general-update-locations-field">
          <gz-policy-panel-general-update-locations-table
            gz-form-input
          ></gz-policy-panel-general-update-locations-table>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="general-update-default-location-field">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.UseBitdefender].label}"
            label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.UseBitdefender].translatedLabel}"
            gz-form-input
            @change="${this.onDefaultLocationChange}"
          ></gz-checkbox>
        </gz-form-field>
      </div>

      <div id="general-update-ring" class="mt-4">
        <gz-title
          id="general-update-ring-title"
          searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.UpdateRing].label}"
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.HEADING_4}"
        >
          ${this.searchableKeywords[GeneralUpdateSearchableKeywords.UpdateRing].translatedLabel}
        </gz-title>

        <gz-text id="general-update-ring-text" fontSize="12" fontWeight="400" class="infoText mt-1"
          >${translateFn(
            'Select an update ring for the target endpoints of this policy. To assign a specific product version to staging rings, use the settings in Configuration profiles > Update staging.'
          )}
        </gz-text>

        <gz-form-field id="general-update-ring-field" class="mt-2" label="${translateFn('Select update ring')}">
          <gz-single-select
            cannotDelete
            gz-form-input
            .filterOptions=${getGeneralUpdateRingFormOptions()}
          ></<gz-single-select>
        </gz-form-field>
      </div>
    `;
  }

  private renderSchedulerSection(type: GeneralUpdateSchedulerType) {
    const weekDaysFormOptions = getWeekDaysFormOptions();
    const validateEndTime = () => {
      const endTimeField =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productEndTimeField : this.signatureEndTimeField;
      endTimeField.validate();
    };
    const validateStartTime = () => {
      const startTimeField =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productStartTimeField : this.signatureStartTimeField;
      startTimeField.validate();
    };
    const occurrenceSelectionChange = (e: CustomEvent) => {
      const oldValue = e.detail.oldValue ? e.detail.oldValue.value : undefined;
      const newValue = e.detail.newValue ? e.detail.newValue.value : undefined;
      // Do nothing if the value is the same
      if (oldValue === newValue) {
        return;
      }

      const weekDaysField =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productWeekDaysField : this.signatureWeekDaysField;
      const startTimeField =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productStartTimeField : this.signatureStartTimeField;
      const endTimeField =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productEndTimeField : this.signatureEndTimeField;
      const timeSection =
        type === GeneralUpdateSchedulerType.PRODUCT ? this.productTimeSection : this.signatureTimeSection;

      switch (newValue) {
        case RepeatInterval.HOURS:
          toggleVisibility(weekDaysField, false);
          toggleVisibility(timeSection, false);
          break;
        case RepeatInterval.DAYS:
          toggleVisibility(weekDaysField, false);
          toggleVisibility(timeSection, true);
          break;
        case RepeatInterval.WEEKDAYS:
          toggleVisibility(weekDaysField, true);
          toggleVisibility(timeSection, true);
          break;
      }

      // wait for component to be ready
      if (!this.isReady) {
        return;
      }

      // reset values
      weekDaysField.value = [
        WeekDays.Sunday,
        WeekDays.Monday,
        WeekDays.Tuesday,
        WeekDays.Wednesday,
        WeekDays.Thursday,
        WeekDays.Friday,
        WeekDays.Saturday,
      ];
      startTimeField.value = { hours: 0, minutes: 0 };
      endTimeField.value = { hours: 23, minutes: 59 };

      // validate start and end time to hide/show error message
      validateStartTime();
      validateEndTime();
    };

    return html` <gz-form-field
        id="general-update-${type}-occurrence-field"
        class="mt-2"
        label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Recurrence].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralUpdateSearchableKeywords.Recurrence].label}"
      >
        <gz-dropdown
          .labelWidth="${LabelWidthValues.MEDIUM}"
          gz-form-input
          .options=${getRepeatIntervalFormOptionsForSelection()}
          @optionSelected="${occurrenceSelectionChange}"
        ></gz-dropdown>
      </gz-form-field>

      <gz-form-field id="general-update-${type}-week-days-field" label="${translateFn('On')}">
        <gz-multiselect-group gz-form-input .options=${weekDaysFormOptions}></gz-multiselect-group>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <div class="d-flex" id="general-update-${type}-time-section">
        <gz-form-field id="general-update-${type}-start-time-field" label="${translateFn('Interval')}">
          <gz-time-picker gz-form-input @change="${validateEndTime}"></gz-time-picker>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <div class="ml-2 mr-2 line">-</div>

        <gz-form-field id="general-update-${type}-end-time-field">
          <gz-time-picker gz-form-input @change="${validateStartTime}"></gz-time-picker>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <gz-form-field id="general-update-${type}-update-interval-field" label="${translateFn('Update interval')}">
        <gz-input-number
          gz-form-input
          .min=${MIN_UPDATE_INTERVAL}
          .max=${MAX_UPDATE_INTERVAL}
          unitSuffix="${translateFn('hours')}"
          inputWidth=${InputWidthValues.MEDIUM}
        ></gz-input-number>
      </gz-form-field>`;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.general.update
   */
  protected getValueFromManager(): GeneralUpdateFormValue {
    // get default value
    const defaultValue = GENERAL_UPDATE_DEFAULT_VALUE;

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          [SectionId.UPDATE]: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.update'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          update: {
            settings: {
              productUpdateScheduler: {
                enabled: this.productEnabledField,
                occurrence: this.productOccurrenceField,
                updateInterval: this.productUpdateIntervalField,
                weekDays: {
                  days: this.productWeekDaysField,
                  startTime: this.productStartTimeField,
                  endTime: this.productEndTimeField,
                },
                postponeReboot: this.productPostponeRebootField,
                rebootAfterInstalling: {
                  enable: this.productRebootEnableField,
                  settings: {
                    day: this.productRebootDayField,
                    time: this.productRebootTimeField,
                  },
                },
              },
              signatureUpdateScheduler: {
                enabled: this.signatureEnabledField,
                occurrence: this.signatureOccurrenceField,
                updateInterval: this.signatureUpdateIntervalField,
                weekDays: {
                  days: this.signatureWeekDaysField,
                  startTime: this.signatureStartTimeField,
                  endTime: this.signatureEndTimeField,
                },
              },
              updateRing: this.updateRingField,
              defaultLocation: this.defaultLocationField,
            },
            updateLocations: this.updateLocationsField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        general: {
          update: {
            settings: {
              productUpdateScheduler: {
                enabled: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.enabled,
                occurrence: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.occurrence,
                updateInterval: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.updateInterval,
                weekDays: {
                  days: [...this.valueT.uiSettings.general.update.settings.productUpdateScheduler.weekDays.days],
                  startTime: {
                    hours: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.weekDays.startHour,
                    minutes: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.weekDays.startMinute,
                  },
                  endTime: {
                    hours: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.weekDays.endHour,
                    minutes: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.weekDays.endMinute,
                  },
                },
                postponeReboot: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.postponeReboot,
                rebootAfterInstalling: {
                  enable:
                    this.valueT.uiSettings.general.update.settings.productUpdateScheduler.rebootAfterInstalling.enable,
                  settings: {
                    day: this.valueT.uiSettings.general.update.settings.productUpdateScheduler.rebootAfterInstalling
                      .settings.day,
                    time: {
                      hours:
                        this.valueT.uiSettings.general.update.settings.productUpdateScheduler.rebootAfterInstalling
                          .settings.hour,
                      minutes:
                        this.valueT.uiSettings.general.update.settings.productUpdateScheduler.rebootAfterInstalling
                          .settings.minutes,
                    },
                  },
                },
              },
              signatureUpdateScheduler: {
                enabled: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.enabled,
                occurrence: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.occurrence,
                updateInterval: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.updateInterval,
                weekDays: {
                  days: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.weekDays.days,
                  startTime: {
                    hours: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.weekDays.startHour,
                    minutes:
                      this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.weekDays.startMinute,
                  },
                  endTime: {
                    hours: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.weekDays.endHour,
                    minutes: this.valueT.uiSettings.general.update.settings.signatureUpdateScheduler.weekDays.endMinute,
                  },
                },
              },
              updateRing: this.valueT.uiSettings.general.update.settings.updateRing,
              defaultLocation: this.valueT.uiSettings.general.update.settings.defaultLocation,
            },
            updateLocations: this.valueT.uiSettings.general.update.updateLocations,
          },
        },
      },
    };
  }

  /**
   * Set validators for form fields.
   */
  protected setValidators() {
    const productTimeValidators = (schedulerType: GeneralUpdateSchedulerType) => [
      CustomizableValidator(() => {
        const startTimeField =
          schedulerType === GeneralUpdateSchedulerType.PRODUCT
            ? this.productStartTimeField
            : this.signatureStartTimeField;
        const endTimeField =
          schedulerType === GeneralUpdateSchedulerType.PRODUCT ? this.productEndTimeField : this.signatureEndTimeField;
        const startMinutes = startTimeField.value.hours * 60 + startTimeField.value.minutes;
        const endMinutes = endTimeField.value.hours * 60 + endTimeField.value.minutes;

        return startMinutes >= endMinutes
          ? { valid: false, error: translateFn('Start time must be before end time.') }
          : { valid: true };
      }),
    ];

    // set validators for update product scheduler
    this.productWeekDaysField.Validators = [RequiredValidator(translateFn('Select at least one day of the week.'))];
    this.productStartTimeField.Validators = productTimeValidators(GeneralUpdateSchedulerType.PRODUCT);
    this.productEndTimeField.Validators = productTimeValidators(GeneralUpdateSchedulerType.PRODUCT);

    // set validators for update signature scheduler
    this.signatureWeekDaysField.Validators = [RequiredValidator(translateFn('Select at least one day of the week.'))];
    this.signatureStartTimeField.Validators = productTimeValidators(GeneralUpdateSchedulerType.SIGNATURE);
    this.signatureEndTimeField.Validators = productTimeValidators(GeneralUpdateSchedulerType.SIGNATURE);
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // compute time field for product & signature update scheduler
    const convertSchedulerTime = (schedulerPath: string[]) => {
      let time = _.get(value, [...schedulerPath, 'weekDays', 'startTime']);
      _.set(value, [...schedulerPath, 'weekDays', 'startHour'], time['hours']);
      _.set(value, [...schedulerPath, 'weekDays', 'startMinute'], time['minutes']);
      _.unset(value, [...schedulerPath, 'weekDays', 'startTime']);

      time = _.get(value, [...schedulerPath, 'weekDays', 'endTime']);
      _.set(value, [...schedulerPath, 'weekDays', 'endHour'], time['hours']);
      _.set(value, [...schedulerPath, 'weekDays', 'endMinute'], time['minutes']);
      _.unset(value, [...schedulerPath, 'weekDays', 'endTime']);
    };
    convertSchedulerTime(['uiSettings', 'general', 'update', 'settings', 'productUpdateScheduler']);
    convertSchedulerTime(['uiSettings', 'general', 'update', 'settings', 'signatureUpdateScheduler']);

    // compute time field for reboot settings
    const rebootPath = [
      'uiSettings',
      'general',
      'update',
      'settings',
      'productUpdateScheduler',
      'rebootAfterInstalling',
    ];
    const timeValue = _.get(value, [...rebootPath, 'settings', 'time']);
    _.set(value, [...rebootPath, 'settings', 'hour'], timeValue['hours']);
    _.set(value, [...rebootPath, 'settings', 'minutes'], timeValue['minutes']);
    _.unset(value, [...rebootPath, 'settings', 'time']);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings'], {}) as FormGroupValue;
    }

    return value;
  }

  private onDefaultLocationChange(e: CustomEvent) {
    const value = e.detail.checked;

    this.updateLocationsField.Validators = value
      ? []
      : [
          RequiredValidator(
            'Define at least one update server location or select "Use Bitdefender Public Update Server as fallback".'
          ),
        ];
    this.updateLocationsField.validate(true);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    if (value) {
      return;
    }

    // open dialog get response
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>
          ${translateFn(
            'Disabling this option prevents automatic updates, leaving your network vulnerable when alternative locations are unavailable.'
          )}
        </p>`;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
      <gz-button id="buttonOne" type="outline" color="negative">${translateFn('Disable')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
    `;
    const footerBtnOne = dialogFooter.querySelector('#buttonOne');
    const footerBtnTwo = dialogFooter.querySelector('#buttonTwo');

    // cancel handler
    const onCancelHandler = () => {
      this.defaultLocationField.value = true;
      dialog.dismiss();
    };
    const dialogConfigs = {
      headline: translateFn('Disable Bitdefender Public Update Server as fallback location?'),
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
      onCloseHandler: onCancelHandler,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', onCancelHandler);
    }
  }

  private toggleRebootSettings() {
    const isDisabled = !this.productPostponeRebootField.value || !this.productRebootEnableField.value;
    toggleDisabled(this.productRebootDayField, isDisabled);
    toggleDisabled(this.productRebootTimeField, isDisabled);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_UPDATE: GzGeneralUpdateWebComponent;
  }
}
